<script setup lang="ts">
import BaseDialog from '@/components/ui/BaseDialog.vue'
import LoginFields from './LoginFields.vue'

/**
 * 「這件事要先登入」對話框。
 *
 * 不是單純的提示 —— **表單就在裡面**。只提示「請先登入」再把人丟去
 * `/login` 的話，使用者付出的代價是離開現在這一頁；而他被擋下的那件事
 * （點的那款遊戲、要存的那筆錢）也得靠 `redirect` 參數繞一圈才回得來。
 *
 * 說明文字依原因而不同（`reasonText`），因為「登录后即可进入游戏」與
 * 「登录后即可存款」對使用者是兩件事，同一句通用文案等於沒說。
 *
 * 展示元件：狀態全部由 `layouts/GlobalGates.vue` 經 `useLoginForm` 供應。
 */
defineProps<{
  open: boolean
  reasonText: string
  submitting: boolean
  formError: string | null
  fieldErrors: Record<string, string>
  captchaSvg: string | null
  captchaLoading: boolean
  captchaFailed: boolean
}>()

defineEmits<{ close: []; submit: []; refreshCaptcha: []; register: [] }>()

const username = defineModel<string>('username', { required: true })
const password = defineModel<string>('password', { required: true })
const captchaAnswer = defineModel<string>('captchaAnswer', { required: true })
</script>

<template>
  <BaseDialog
    :open="open"
    :title="$t('authGate.title')"
    :busy="submitting"
    @close="$emit('close')"
  >
    <p class="gate__reason">{{ reasonText }}</p>

    <LoginFields
      v-model:username="username"
      v-model:password="password"
      v-model:captcha-answer="captchaAnswer"
      :submitting="submitting"
      :form-error="formError"
      :field-errors="fieldErrors"
      :captcha-svg="captchaSvg"
      :captcha-loading="captchaLoading"
      :captcha-failed="captchaFailed"
      @submit="$emit('submit')"
      @refresh-captcha="$emit('refreshCaptcha')"
    />

    <!-- 沒帳號的人在這裡是死路，所以一定要有這條出口。
         按了會關掉對話框並導到註冊頁 —— 註冊是多步驟流程，塞不進彈窗 -->
    <p class="gate__switch">
      {{ $t('auth.noAccount') }}
      <button type="button" class="gate__link" :disabled="submitting" @click="$emit('register')">
        {{ $t('auth.goRegister') }}
      </button>
    </p>
  </BaseDialog>
</template>

<style scoped lang="scss">
.gate {
  &__reason {
    margin: 0 0 $space-5;
    color: $color-text-dim;
    font-size: $text-sm;
    line-height: 1.6;
  }

  &__switch {
    margin: $space-6 0 0;
    padding-top: $space-4;
    border-top: 1px solid $color-panel-border;
    text-align: center;
    color: $color-text-dim;
    font-size: $text-sm;
  }

  // 語意上是動作（開另一個頁面並關掉這個），所以是 button 不是連結，
  // 但外觀要跟登入頁底下那條「去註冊」一致
  &__link {
    padding: 0;
    border: 0;
    background: none;
    color: $color-brand;
    font: inherit;
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
